import { promises as fs } from 'fs';
import { join, resolve } from 'path';
import { FileSystemError } from '@/types';
import { ValidationUtils } from '@/utils/validation';

/**
 * Validate and resolve a file or directory path.
 */
export function resolvePath(p: string, context: string): string {
  ValidationUtils.validateFilePath(p, context);
  return resolve(p);
}

/**
 * Check if a path exists.
 */
export async function exists(p: string): Promise<boolean> {
  try {
    await fs.stat(p);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if the given path is a directory.
 * Returns false if the path doesn't exist or is not accessible.
 */
export async function isDirectory(p: string): Promise<boolean> {
  try {
    const stats = await fs.stat(p);
    return stats.isDirectory();
  } catch (_error) {
    // Return false if path doesn't exist or is not accessible
    // This allows graceful handling in discovery logic
    return false;
  }
}

/**
 * Check if the given path is a file.
 * Returns false if the path doesn't exist or is not accessible.
 */
export async function isFile(p: string): Promise<boolean> {
  try {
    const stats = await fs.stat(p);
    return stats.isFile();
  } catch (_error) {
    // Return false if path doesn't exist or is not accessible
    // This allows graceful handling in discovery logic
    return false;
  }
}

/**
 * Read directory contents and return full paths.
 */
export async function readDir(p: string): Promise<string[]> {
  try {
    const entries = await fs.readdir(p);
    return entries.map(name => join(p, name));
  } catch (error) {
    throw new FileSystemError(`Failed to read directory: ${p}`, error as Error);
  }
}

/**
 * Read file as text.
 */
export async function readFile(p: string): Promise<string> {
  try {
    return await fs.readFile(p, 'utf-8');
  } catch (error) {
    throw new FileSystemError(`Failed to read file: ${p}`, error as Error);
  }
}

/**
 * Read file as Buffer.
 */
export async function readFileBuffer(p: string): Promise<Buffer> {
  try {
    return await fs.readFile(p);
  } catch (error) {
    throw new FileSystemError(
      `Failed to read file buffer: ${p}`,
      error as Error
    );
  }
}
